import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';

export type DialogData = {
  case_uuid: string;
  payee_case_reference_id: string;
};

type DocumentCounts = {
  event_attachment_count: number;
  legal_relationship_contract_count: number;
  claim_file_count: number;
};

@Component({
  selector: 'payee-download-case-documents-dialog',
  templateUrl: './download-case-documents-dialog.component.html',
  styleUrls: ['./download-case-documents-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    PayeeButtonComponent,
    MatDialogClose,
    TranslateModule,
  ],
})
export class DownloadCaseDocumentsDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private dialogRef =
    inject<MatDialogRef<DownloadCaseDocumentsDialogComponent>>(MatDialogRef);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);

  counts?: DocumentCounts;
  downloading = false;

  readonly form = this.fb.group({
    event_attachments: this.fb.control(true, { nonNullable: true }),
    legal_relationship_contract: this.fb.control(true, { nonNullable: true }),
    claim_files: this.fb.control(true, { nonNullable: true }),
  });

  get hasAnyDocuments(): boolean {
    if (!this.counts) {
      return false;
    }

    return (
      this.counts.event_attachment_count > 0 ||
      this.counts.legal_relationship_contract_count > 0 ||
      this.counts.claim_file_count > 0
    );
  }

  get canDownload(): boolean {
    if (!this.hasAnyDocuments || this.downloading) {
      return false;
    }

    const { event_attachments, legal_relationship_contract, claim_files } =
      this.form.getRawValue();

    return event_attachments || legal_relationship_contract || claim_files;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.counts = await this.getCounts(this.data.case_uuid);
      this.form.patchValue({
        event_attachments: this.counts.event_attachment_count > 0,
        legal_relationship_contract:
          this.counts.legal_relationship_contract_count > 0,
        claim_files: this.counts.claim_file_count > 0,
      });
      this.disableControlsWithNoFiles();
    } catch (error) {
      console.error('Error while loading case documents summary', error);
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.DOWNLOAD_DOCUMENTS_DIALOG.LOAD_ERROR',
        ),
        this.translate.instant('OK'),
        { duration: 5000 },
      );
      this.onCancel();
    }
  }

  onCancel = (): void => {
    this.dialogRef.close();
  };

  async onDownload(): Promise<void> {
    if (!this.canDownload) {
      return;
    }

    this.downloading = true;
    try {
      await this.downloadZip();
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error while downloading case documents', error);
      this.snackbar.open(
        this.translate.instant(
          'CASES_TABLE.DOWNLOAD_DOCUMENTS_DIALOG.DOWNLOAD_ERROR',
        ),
        this.translate.instant('OK'),
        { duration: 5000 },
      );
    } finally {
      this.downloading = false;
    }
  }

  private async getCounts(caseId: string): Promise<DocumentCounts> {
    const url = `${environment.baseUrl}/api/payee-case/${caseId}/documents-summary`;
    const result = await axios.get<DocumentCounts>(url);

    return result.data;
  }

  private async downloadZip(): Promise<void> {
    const url = `${environment.baseUrl}/api/payee-case/${this.data.case_uuid}/download-documents`;
    const response = await axios.post(url, this.form.getRawValue(), {
      responseType: 'blob',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(response.data);
    link.download = `${this.data.payee_case_reference_id}-documents.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private disableControlsWithNoFiles(): void {
    if (!this.counts) {
      return;
    }

    if (this.counts.event_attachment_count === 0) {
      this.form.controls.event_attachments.disable();
    }
    if (this.counts.legal_relationship_contract_count === 0) {
      this.form.controls.legal_relationship_contract.disable();
    }
    if (this.counts.claim_file_count === 0) {
      this.form.controls.claim_files.disable();
    }
  }
}
